import { authedFetch } from "@/lib/authed-fetch";
import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, GripVertical, Edit2, Check, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { StatusConfig } from "@shared/schema";

interface EditableStatus extends StatusConfig {
  isEditing?: boolean;
  tempDisplayName?: string;
  tempColor?: string;
}

export function StatusButtonsSettings() {
  const [statuses, setStatuses] = useState<Record<string, EditableStatus[]>>({
    project: [],
    task: [],
    pulse: []
  });
  const [activeTab, setActiveTab] = useState<"project" | "task" | "pulse">("project");
  const [newStatus, setNewStatus] = useState({ displayName: "", color: "#6B7280" });
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: 'edit' | 'delete';
    status?: EditableStatus;
    onConfirm?: () => void;
  }>({ open: false, type: 'edit' });

  // Fetch status configurations
  const { data: statusConfigs = [] } = useQuery<StatusConfig[]>({
    queryKey: ["/api/status-configs"],
  });

  // Group statuses by category
  useEffect(() => {
    if (statusConfigs.length > 0) {
      const grouped = statusConfigs.reduce((acc, status) => {
        if (!acc[status.category]) acc[status.category] = [];
        acc[status.category].push(status);
        return acc;
      }, {} as Record<string, StatusConfig[]>);
      
      setStatuses(prev => {
        // Preserve the editing state when updating from database
        const preserveEditingState = (category: string, newStatuses: StatusConfig[]): EditableStatus[] => {
          return newStatuses.map(newStatus => {
            const existingStatus = prev[category]?.find(s => s.id === newStatus.id);
            if (existingStatus?.isEditing) {
              return {
                ...newStatus,
                isEditing: true,
                tempDisplayName: existingStatus.tempDisplayName,
                tempColor: existingStatus.tempColor
              };
            }
            return newStatus;
          });
        };

        return {
          project: preserveEditingState('project', grouped.project || []),
          task: preserveEditingState('task', grouped.task || []),
          pulse: preserveEditingState('pulse', grouped.pulse || [])
        };
      });
    }
  }, [statusConfigs]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (data: { displayName: string; color: string; category: string }) => {
      const response = await authedFetch("/api/status-configs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...data,
          statusKey: data.displayName.toLowerCase().replace(/\s+/g, "_"),
          sortOrder: statuses[data.category]?.length || 0
        }),
      });
      if (!response.ok) throw new Error("Failed to create status");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/status-configs"] });
      toast({ description: "Status created successfully" });
      setNewStatus({ displayName: "", color: "#6B7280" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<StatusConfig> }) => {
      const response = await authedFetch(`/api/status-configs/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error("Failed to update status");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/status-configs"] });
      toast({ description: "Status updated successfully" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await authedFetch(`/api/status-configs/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete status");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/status-configs"] });
      toast({ description: "Status deleted successfully" });
    },
    onError: (error: Error) => {
      toast({ 
        description: error.message,
        variant: "destructive" 
      });
    },
  });

  const handleCreate = () => {
    if (!newStatus.displayName.trim()) {
      toast({ description: "Please enter a status name", variant: "destructive" });
      return;
    }
    createMutation.mutate({
      displayName: newStatus.displayName,
      color: newStatus.color,
      category: activeTab
    });
  };

  const startEditing = (status: EditableStatus) => {
    setStatuses(prev => ({
      ...prev,
      [activeTab]: prev[activeTab].map(s => 
        s.id === status.id 
          ? { ...s, isEditing: true, tempDisplayName: s.displayName, tempColor: s.color }
          : s
      )
    }));
  };

  const cancelEditing = (status: EditableStatus) => {
    setStatuses(prev => ({
      ...prev,
      [activeTab]: prev[activeTab].map(s => 
        s.id === status.id 
          ? { ...s, isEditing: false, tempDisplayName: undefined, tempColor: undefined }
          : s
      )
    }));
  };

  const saveEdit = (status: EditableStatus) => {
    // For system statuses, skip display name validation since it can't be changed
    if (!status.isSystem && !status.tempDisplayName?.trim()) return;
    
    setConfirmDialog({
      open: true,
      type: 'edit',
      status,
      onConfirm: () => {
        // For system statuses, only allow color changes
        if (status.isSystem) {
          updateMutation.mutate({
            id: status.id,
            data: {
              color: status.tempColor
            }
          });
        } else {
          // For custom statuses, allow both name and color changes
          updateMutation.mutate({
            id: status.id,
            data: {
              displayName: status.tempDisplayName,
              color: status.tempColor
            }
          });
        }
        
        setStatuses(prev => ({
          ...prev,
          [activeTab]: prev[activeTab].map(s => 
            s.id === status.id 
              ? { ...s, isEditing: false, tempDisplayName: undefined, tempColor: undefined }
              : s
          )
        }));
      }
    });
  };

  const confirmDelete = (status: EditableStatus) => {
    setConfirmDialog({
      open: true,
      type: 'delete',
      status,
      onConfirm: () => {
        deleteMutation.mutate(status.id);
      }
    });
  };

  const renderStatusRow = (status: EditableStatus) => {
    const isEditing = status.isEditing;

    return (
      <div key={status.id} className="flex items-center gap-3 p-3 border border-gray-600 rounded-lg hover:bg-gray-800">
        <GripVertical className="w-4 h-4 text-gray-500 cursor-move" />
        
        {isEditing ? (
          <>
            {status.isSystem ? (
              // System status: only show display name (read-only) and color picker
              <>
                <div className="flex-1 px-3 py-1 text-sm text-gray-400 bg-gray-800 rounded">
                  {status.displayName} <span className="text-xs text-gray-500">(system)</span>
                </div>
                <Input
                  type="color"
                  value={status.tempColor}
                  onChange={(e) => setStatuses(prev => ({
                    ...prev,
                    [activeTab]: prev[activeTab].map(s => 
                      s.id === status.id ? { ...s, tempColor: e.target.value } : s
                    )
                  }))}
                  className="w-20 h-8"
                />
              </>
            ) : (
              // Custom status: allow both name and color editing
              <>
                <Input
                  value={status.tempDisplayName}
                  onChange={(e) => setStatuses(prev => ({
                    ...prev,
                    [activeTab]: prev[activeTab].map(s => 
                      s.id === status.id ? { ...s, tempDisplayName: e.target.value } : s
                    )
                  }))}
                  className="flex-1 h-8"
                />
                <Input
                  type="color"
                  value={status.tempColor}
                  onChange={(e) => setStatuses(prev => ({
                    ...prev,
                    [activeTab]: prev[activeTab].map(s => 
                      s.id === status.id ? { ...s, tempColor: e.target.value } : s
                    )
                  }))}
                  className="w-20 h-8"
                />
              </>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => saveEdit(status)}
              className="h-8 w-8 p-0"
            >
              <Check className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => cancelEditing(status)}
              className="h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </>
        ) : (
          <>
            <div 
              className="px-3 py-1 rounded text-sm font-medium"
              style={{ backgroundColor: status.color, color: '#fff' }}
            >
              {status.displayName || '\u00A0'}
              {status.isSystem && <span className="ml-2 text-xs opacity-75">(system)</span>}
            </div>
            <div className="flex-1" />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => startEditing(status)}
              className="h-8 w-8 p-0"
            >
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => confirmDelete(status)}
              className="h-8 w-8 p-0 hover:text-red-400"
              disabled={!!status.isSystem}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-400">
        Manage status options for projects, tasks, and pulses. System statuses can only have their colors changed, while custom statuses can be fully edited or deleted.
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="project">Project Status</TabsTrigger>
          <TabsTrigger value="task">Task Status</TabsTrigger>
          <TabsTrigger value="pulse">Pulse Status</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {/* Show initialization button for project statuses if none exist */}
          {activeTab === 'project' && statuses.project.length === 0 && (
            <div className="p-4 border border-gray-600 rounded-lg bg-gray-800/50">
              <p className="text-sm text-gray-300 mb-3">
                No project statuses found. Initialize the system-defined project statuses.
              </p>
              <Button
                onClick={async () => {
                  try {
                    const response = await authedFetch('/api/status-configs/initialize-project', {
                      method: 'POST',
                    });
                    if (!response.ok) throw new Error('Failed to initialize');
                    queryClient.invalidateQueries({ queryKey: ['/api/status-configs'] });
                    toast({ description: 'Project statuses initialized successfully' });
                  } catch (error) {
                    toast({ 
                      description: 'Failed to initialize project statuses', 
                      variant: 'destructive' 
                    });
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Initialize Project Statuses
              </Button>
            </div>
          )}
          
          <div className="space-y-2">
            {statuses[activeTab].map(renderStatusRow)}
          </div>

          {/* Only show Add New Status for non-project categories */}
          {activeTab !== 'project' && (
            <div className="border-t border-gray-600 pt-4">
              <Label className="text-sm text-gray-400">Add New Status</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="Status name"
                  value={newStatus.displayName}
                  onChange={(e) => setNewStatus(prev => ({ ...prev, displayName: e.target.value }))}
                  className="flex-1"
                />
                <Input
                  type="color"
                  value={newStatus.color}
                  onChange={(e) => setNewStatus(prev => ({ ...prev, color: e.target.value }))}
                  className="w-20"
                />
                <Button
                  onClick={handleCreate}
                  disabled={createMutation.isPending}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AlertDialog 
        open={confirmDialog.open} 
        onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, open }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog.type === 'edit' ? 'Edit Status' : 'Delete Status'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.type === 'edit' ? (
                <>
                  {confirmDialog.status?.isSystem ? (
                    <>
                      Change the color for system status "{confirmDialog.status.displayName}"?
                      <br /><br />
                      <span className="text-xs text-gray-500">Note: Only the color can be changed for system statuses. The name and other properties remain fixed.</span>
                    </>
                  ) : (
                    <>
                      Are you sure you want to edit "{confirmDialog.status?.displayName}"? 
                      <br /><br />
                      <strong>Warning:</strong> This status may be used by existing projects, tasks, or pulses. 
                      Changing it will affect all items currently using this status.
                    </>
                  )}
                </>
              ) : (
                <>
                  Are you sure you want to delete "{confirmDialog.status?.displayName}"? 
                  <br /><br />
                  <strong>Warning:</strong> This status may be used by existing projects, tasks, or pulses. 
                  Deleting it could cause issues with items currently using this status. 
                  Consider editing instead of deleting.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                confirmDialog.onConfirm?.();
                setConfirmDialog({ open: false, type: 'edit' });
              }}
              className={confirmDialog.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {confirmDialog.type === 'edit' ? 'Update' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}